import React, { useState } from 'react';
import { 
  PresentationChartBarIcon, 
  ArrowDownTrayIcon, 
  EyeIcon, 
  ExclamationTriangleIcon,
  ArrowPathIcon 
} from '@heroicons/react/24/outline';
import { useTheme } from '../../features/theme/context/ThemeContext';

const PresentationCard = ({ presentation, messageId, onRefresh }) => {
  const { darkMode } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  
  if (!presentation) return null;

  const { status, title, originalPrompt, errorMessage, embedUrl, downloadUrl } = presentation;

  // Helper function to get the correct API URL
  const getApiUrl = (endpoint) => {
    const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8081/api';
    let baseUrl = apiUrl.endsWith('/') ? apiUrl.slice(0, -1) : apiUrl;
    
    if (baseUrl.endsWith('/api')) {
      const cleanEndpoint = endpoint.startsWith('api/') ? endpoint.substring(4) : endpoint;
      return `${baseUrl}/${cleanEndpoint}`;
    }
    
    const cleanEndpoint = endpoint.startsWith('api/') ? endpoint : `api/${endpoint}`;
    return `${baseUrl}/${cleanEndpoint}`;
  };

  const handleView = () => {
    if (embedUrl) {
      window.open(getApiUrl(`slides/${messageId}/embed`), '_blank');
    }
  };

  const handleDownload = async () => {
    if (!downloadUrl) return;
    
    setIsLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getApiUrl(`slides/${messageId}/download`), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${title || 'presentation'}.pptx`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Download failed');
      }
    } catch (error) {
      console.error('Download error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = () => {
    switch (status) {
      case 'generating':
        return <ArrowPathIcon className="h-5 w-5 animate-spin text-blue-500" />;
      case 'completed':
        return <PresentationChartBarIcon className="h-5 w-5 text-green-500" />;
      case 'failed':
        return <ExclamationTriangleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <PresentationChartBarIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'generating':
        return 'Creating slides...';
      case 'completed':
        return 'Presentation ready';
      case 'failed':
        return 'Generation failed';
      default:
        return 'Unknown status';
    }
  };

  const getBorderColor = () => {
    switch (status) {
      case 'generating':
        return 'border-blue-200 dark:border-blue-800';
      case 'completed':
        return 'border-green-200 dark:border-green-800';
      case 'failed':
        return 'border-red-200 dark:border-red-800';
      default:
        return 'border-gray-200 dark:border-gray-700';
    }
  };

  const getBgColor = () => {
    switch (status) {
      case 'generating':
        return 'bg-blue-50 dark:bg-blue-950/30';
      case 'completed':
        return 'bg-green-50 dark:bg-green-950/30';
      case 'failed':
        return 'bg-red-50 dark:bg-red-950/30';
      default:
        return 'bg-gray-50 dark:bg-gray-900/30';
    }
  };

  return (
    <div className={`max-w-md rounded-2xl border-2 ${getBorderColor()} ${getBgColor()} p-4 transition-all duration-200`}>
      {/* Header */}
      <div className="flex items-center gap-3 mb-3">
        {getStatusIcon()}
        <div className="flex-1">
          <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
            {getStatusText()}
          </h3>
          {title && (
            <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 line-clamp-1">
              {title}
            </p>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="mb-4">
        {status === 'generating' && (
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex space-x-1">
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.1s" }}></div>
              <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: "0.2s" }}></div>
            </div>
            <span>Processing your request...</span>
          </div>
        )}

        {status === 'completed' && (
          <div className="text-sm text-gray-700 dark:text-gray-300">
            <p className="font-medium mb-1">✅ Your presentation is ready!</p>
            <p className="text-xs text-gray-600 dark:text-gray-400">
              Based on: "{originalPrompt?.substring(0, 60)}{originalPrompt?.length > 60 ? '...' : ''}"
            </p>
          </div>
        )}

        {status === 'failed' && (
          <div className="text-sm text-red-700 dark:text-red-300">
            <p className="font-medium mb-1">❌ Generation failed</p>
            {errorMessage && (
              <p className="text-xs text-red-600 dark:text-red-400">
                {errorMessage}
              </p>
            )}
            {onRefresh && (
              <button
                onClick={() => onRefresh(originalPrompt)}
                className="text-xs text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200 underline mt-1"
              >
                Try again
              </button>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      {status === 'completed' && (
        <div className="flex gap-2">
          <button
            onClick={handleView}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors duration-200"
          >
            <EyeIcon className="h-4 w-4" />
            View
          </button>
          <button
            onClick={handleDownload}
            disabled={isLoading}
            className="flex-1 flex items-center justify-center gap-2 px-3 py-2 bg-gray-600 hover:bg-gray-700 disabled:bg-gray-400 text-white text-sm font-medium rounded-lg transition-colors duration-200"
          >
            {isLoading ? (
              <ArrowPathIcon className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowDownTrayIcon className="h-4 w-4" />
            )}
            Download
          </button>
        </div>
      )}

      {status === 'generating' && (
        <div className="flex justify-center">
          <div className="px-4 py-2 bg-blue-100 dark:bg-blue-900/50 text-blue-700 dark:text-blue-300 text-sm rounded-lg">
            Please wait while we create your slides...
          </div>
        </div>
      )}
    </div>
  );
};

export default PresentationCard; 